import { useSelector } from "react-redux";
import { useAccount, useReadContracts } from "wagmi";
import useDappConfig from "./useDappConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { formatUnits, maxUint256 } from "viem";
import { ETH } from "@config/contractsConfig";
import { useEffect, useMemo } from "react";
import useDappChain from "./useDappChain";

const useVaultAllowance = () => {
  const { selectedERC20Vault } = useSelector((state: any) => state.user);
  const { isTransactionProcessing } = useSelector((state: any) => state.stake);
  const { address: walletAddress } = useAccount();
  const { chain, isEthereumChain } = useDappChain();

  const { config, vaultsContractConfig } = useDappConfig();

  const token = useMemo(() => {
    let token: any = isEthereumChain
      ? config.tokens[selectedERC20Vault]
      : config.nativeToken;

    if (selectedERC20Vault === config.xtoken.symbol) {
      token = {
        ...config.xtoken,
        tags: [],
      };
    }
    return token;
  }, [config, selectedERC20Vault]);

  const allowanceContracts = useMemo(() => {
    const contracts = [];
    const vaultConfig = isEthereumChain
      ? vaultsContractConfig[chain.id]?.rsETHAdapterPool
      : vaultsContractConfig[chain.id]?.interactionPool;

    contracts.push({
      ...token,
      functionName: "allowance",
      args: [walletAddress, vaultConfig?.address],
    });

    return contracts;
  }, [walletAddress, chain, token, vaultsContractConfig]);

  const { data, refetch: refetchAllowance } = useReadContracts({
    contracts: [...allowanceContracts],
    query: {
      enabled: !!walletAddress,
      refetchInterval: 60_000,
    },
  });

  useEffect(() => {
    if (!isTransactionProcessing) {
      refetchAllowance();
    }
  }, [isTransactionProcessing]);

  const allowanceData = useMemo(() => {
    if (data && walletAddress) {
      return {
        erc20Allowance:
          selectedERC20Vault !== ETH
            ? +toFixedWithoutRounding(
                data[0]?.result
                  ? formatUnits(data[0].result as bigint, token.decimals)
                  : "0",
                8
              )
            : +toFixedWithoutRounding(
                formatUnits(maxUint256, config.nativeToken.decimals),
                8
              ),
      };
    }
    return {
      erc20Allowance: 0,
    };
  }, [data, walletAddress, selectedERC20Vault]);

  return allowanceData;
};

export default useVaultAllowance;
